import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { LexicalEditor } from 'lexical';
import { $getRoot } from 'lexical';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';

import { parseReplacementWithPauses, wordsOf } from './pause-tags';
import {
    computeSceneGroupShortIds,
    resolveSceneGroupById,
    resolveSceneGroupHandle,
} from './scene-group-addressing';
import {
    $buildTokenStream,
    buildPatternTokens,
    findTokenSequenceMatches,
} from './transcript-pause-staging';
import { normalizeTranscriptToken } from './transcript-word-match';
import { VideoStateTool } from './VideoStateTool';
import { AI_VOICEOVER_DISABLED_MESSAGE } from './constants';

import { getCanUseAIVoiceover } from 'js/src/data/account/account-selectors';
import { MAX_PRONUNCIATION_WORDS } from 'js/src/data/pronunciations/pronunciations-types';
import {
    editFullAIVoiceoverOperation,
    getEditorFromSceneGroupId,
    setWordLevelPronunciation,
} from 'js/src/data/transcript';
import {
    $isTranscriptWordNode,
    type TranscriptWordNode,
} from 'js/src/data/transcript/nodes/TranscriptWordNode';
import { updateEditor } from 'js/src/data/transcript/transcript-utils';
import { refreshFirstScenePreviewOperation } from 'js/src/data/video-planner';
import type { RootState } from 'js/src/libs/RootState';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import {
    getVoiceDrivenBaseAssetCutRegions,
    isAIVoiceoverSceneGroup,
} from 'js/src/luminary/luminary-selectors';

// Same cap as the editor's "Fix pronunciation" modal input.
const MAX_PRONUNCIATION_LENGTH = 100;

export const fixPronunciationSchema = z.object({
    scene_group_id: z
        .string()
        .describe(
            'The stable scene_group_id of the AI voiceover scene group (from the video state).',
        ),
    phrase: z
        .string()
        .describe(
            `The word or phrase (1-${MAX_PRONUNCIATION_WORDS} consecutive words) to re-pronounce, written exactly as it appears in the transcript. Every occurrence in the scene group is updated.`,
        ),
    pronunciation: z
        .string()
        .max(MAX_PRONUNCIATION_LENGTH)
        .describe(
            'How the voice should say the phrase, as a phonetic respelling (e.g. "LOO-men five"). An empty string removes the override so the phrase is read as written again.',
        ),
});
export type FixPronunciationArgs = z.infer<typeof fixPronunciationSchema>;

const comparableText = (text: string) => wordsOf(text).map(normalizeTranscriptToken).join(' ');

type WordOverrides = Map<string, { pronunciation?: string; pronunciationId?: string }>;

const readWordOverrides = (editor: LexicalEditor): WordOverrides =>
    editor.getEditorState().read(() => {
        const overrides: WordOverrides = new Map();
        for (const node of $getRoot().getAllTextNodes()) {
            if ($isTranscriptWordNode(node)) {
                overrides.set(node.id, {
                    pronunciation: node.pronunciation,
                    pronunciationId: node.pronunciationId,
                });
            }
        }
        return overrides;
    });

const restoreWordOverrides = (editor: LexicalEditor, overrides: WordOverrides) =>
    updateEditor(editor, () => {
        for (const node of $getRoot().getAllTextNodes()) {
            if (!$isTranscriptWordNode(node)) {
                continue;
            }
            const previous = overrides.get(node.id);
            if (
                previous &&
                (node.pronunciation !== previous.pronunciation ||
                    node.pronunciationId !== previous.pronunciationId)
            ) {
                node.pronunciation = previous.pronunciation;
                node.pronunciationId = previous.pronunciationId;
            }
        }
    });

export class FixPronunciationTool extends VideoStateTool<typeof fixPronunciationSchema> {
    protected override readonly schema = fixPronunciationSchema;
    protected override readonly toolName = TOOL_NAMES.fixPronunciation;
    protected override readonly toolDescription = `Change how the AI voiceover pronounces a word or phrase in this video, then re-voice it. The transcript and captions keep the original spelling. Only use when the user reports a mispronunciation or asks for a specific pronunciation — never proactively. Load the \`${SKILL_NAMES.aiVoiceoverVideoBasics}\` skill for how to write a pronunciation.`;

    public override getToolDisplayName(): string {
        return 'Fixing pronunciation';
    }

    public get statusIcon(): string {
        return 'record_voice_over';
    }

    protected override onEditApplied(args: FixPronunciationArgs): void {
        const resolved = resolveSceneGroupById(this.luminary, args.scene_group_id);
        if (!('error' in resolved) && resolved.index === 0) {
            this.dispatch(refreshFirstScenePreviewOperation());
        }
    }

    public async execute(args: FixPronunciationArgs, _context: ToolExecutionContext) {
        if (!getCanUseAIVoiceover(this.getState())) {
            return `Error: ${AI_VOICEOVER_DISABLED_MESSAGE}`;
        }

        const { sceneGroup } = resolveSceneGroupHandle(this.luminary, args);
        const shortId =
            computeSceneGroupShortIds(this.luminary.sceneGroups.map((group) => group.id)).get(
                sceneGroup.id,
            ) ?? sceneGroup.id;

        if (!isAIVoiceoverSceneGroup(sceneGroup)) {
            return `Error: Scene group ${shortId} is not an AI voiceover scene group. ${TOOL_NAMES.fixPronunciation} only works on AI voiceover scene groups — talking-head and recorded voiceovers are real audio and can't be re-pronounced.`;
        }

        const { words: phraseWords, pauses } = parseReplacementWithPauses(args.phrase);
        if (pauses.length > 0) {
            return 'Error: phrase must be words only — leave out <pause/> tags.';
        }
        if (phraseWords.length === 0) {
            return 'Error: phrase is empty. Give the word or phrase to re-pronounce.';
        }
        if (phraseWords.length > MAX_PRONUNCIATION_WORDS) {
            return `Error: phrase has ${phraseWords.length} words; a pronunciation covers at most ${MAX_PRONUNCIATION_WORDS} consecutive words. Fix each word or short phrase separately.`;
        }

        const pronunciation = args.pronunciation.trim();
        const isClearing = pronunciation === '';
        if (!isClearing && comparableText(pronunciation) === comparableText(args.phrase)) {
            return `Error: The pronunciation "${pronunciation}" is the same as the written phrase, so the voice would say it exactly as before. Respell it phonetically (e.g. split into syllables and capitalize the stressed one).`;
        }

        const editor = getEditorFromSceneGroupId(this.getState(), sceneGroup.id);
        if (!editor) {
            return `Error: The transcript editor for scene group ${shortId} is not available.`;
        }

        const cutRegions = getVoiceDrivenBaseAssetCutRegions(sceneGroup);
        const pattern = buildPatternTokens({ words: phraseWords, pauses: [] });
        const { occurrenceWordIds, alreadyApplied, straddledGroup } = editor
            .getEditorState()
            .read(() => {
                const stream = $buildTokenStream(cutRegions);
                const matches = findTokenSequenceMatches(stream, pattern);
                const occurrences = matches.map(({ startIndex, endIndex }) =>
                    stream
                        .slice(startIndex, endIndex + 1)
                        .flatMap((token) => (token.kind === 'word' ? [token.node] : [])),
                );
                const groupWords = new Map<string, TranscriptWordNode[]>();
                for (const node of $getRoot().getAllTextNodes()) {
                    if ($isTranscriptWordNode(node) && node.pronunciationId) {
                        const words = groupWords.get(node.pronunciationId) ?? [];
                        words.push(node);
                        groupWords.set(node.pronunciationId, words);
                    }
                }
                // Setting an override rewrites every word of any group it touches, so a group
                // reaching past the occurrence would have its other words absorbed and go unspoken.
                const straddled = occurrences
                    .flatMap((nodes) => {
                        const occurrenceIds = new Set(nodes.map((node) => node.id));
                        return nodes.flatMap((node) => {
                            const group = node.pronunciationId
                                ? groupWords.get(node.pronunciationId)
                                : undefined;
                            return group?.some((member) => !occurrenceIds.has(member.id))
                                ? [group]
                                : [];
                        });
                    })
                    .at(0);
                return {
                    occurrenceWordIds: occurrences.map((nodes) => nodes.map((node) => node.id)),
                    alreadyApplied: occurrences.every(
                        (nodes) =>
                            nodes.every(
                                (node) => node.pronunciationId === nodes[0].pronunciationId,
                            ) && (nodes[0].pronunciation ?? '') === pronunciation,
                    ),
                    straddledGroup: straddled && {
                        phrase: straddled
                            .map((node) => node.getTextContent().trim())
                            .filter(Boolean)
                            .join(' '),
                        pronunciation: straddled[0].pronunciation,
                    },
                };
            });

        if (occurrenceWordIds.length === 0) {
            return `Error: The phrase "${args.phrase}" was not found in the transcript of scene group ${shortId}. Write it exactly as it appears in the transcript.`;
        }
        if (straddledGroup) {
            return `Error: "${args.phrase}" is part of an existing pronunciation override for the phrase "${straddledGroup.phrase}" (pronounced "${straddledGroup.pronunciation}"). Changing only part of it would silence the rest. Call again with phrase "${straddledGroup.phrase}" and either a pronunciation covering all of it, or an empty pronunciation to remove that override.`;
        }
        if (alreadyApplied) {
            return isClearing
                ? `No change: "${args.phrase}" has no pronunciation override in scene group ${shortId}.`
                : `No change: "${args.phrase}" is already pronounced "${pronunciation}" in scene group ${shortId}. If it still sounds wrong, try a different respelling.`;
        }

        const overridesBefore = readWordOverrides(editor);
        try {
            // One call per occurrence: words sharing an override group are spoken as a single
            // phrase, so grouping every occurrence together would say the respelling only once.
            for (const wordIds of occurrenceWordIds) {
                await setWordLevelPronunciation(editor, wordIds, pronunciation);
            }
            const { editFullAIVoiceover } = makeOperationsCallable(
                { editFullAIVoiceoverOperation },
                this.luminary,
                this.dispatch,
                this.getState as () => RootState,
            );
            await editFullAIVoiceover(sceneGroup.id);
        } catch (error) {
            // The audio didn't change, so neither may the transcript: a lingering override would
            // make a retry report the fix as already applied.
            await restoreWordOverrides(editor, overridesBefore);
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error fixing pronunciation: ${errorMessage}. The pronunciation was not changed.`;
        }

        const occurrenceCount = occurrenceWordIds.length;
        const occurrences = `${occurrenceCount} occurrence${occurrenceCount === 1 ? '' : 's'}`;
        return isClearing
            ? `Success: Removed the pronunciation override for "${args.phrase}" (${occurrences}) in scene group ${shortId} and re-voiced it.`
            : `Success: "${args.phrase}" is now pronounced "${pronunciation}" (${occurrences}) in scene group ${shortId}, and the voiceover was re-voiced. You can't hear the result — ask the user to listen and say whether it sounds right.`;
    }
}
